/**
 * The Detections stream, beneath the Alerts queue (task 43, after task 53).
 *
 * Everything the platform noticed, newest first, with what the playbook
 * decided about each: raised into an alert (linked by its BSM id) or
 * resolved by a named rule. Severity, source and outcome are filters on
 * the stream; the score sort and severity key range live on the Alerts
 * queue now. Pages follow the API's opaque cursor. The filters and the
 * date range live in the URL (tasks 88–89); the range is sent as `since`,
 * a key-condition bound on `first_seen`.
 *
 * With no Client selected it is every visible Client's stream merged by
 * time (task 66): each row names its Client, and opening one selects
 * that Client so the detail screen has its context.
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';

import {
  listAllDetections,
  listDetections,
  type CrossClientDetection,
  type Detection,
  type DetectionListParams,
} from '@bluesphere/api-client';
import { PillButton, SEVERITY_COLORS, SeverityChip } from '@bluesphere/ui-kit';

import { useAccessToken } from '../auth/useAccessToken';
import { useSelectedTenant } from '../auth/useSelectedTenant';
import { orgLabel } from '../lib/orgs';
import {
  SeverityPills,
  SeveritySelect,
  filterSelectClass,
  type SeverityFilterValue,
} from '../components/SeverityFilter';
import { ClientBadge, EmptyState, ErrorState, Loading, SourceBadge } from '../components/states';
import { outcomeLabel, sourceLabel, timeAgo } from '@bluesphere/mdr-alert-panel';
import {
  OUTCOME_OPTIONS,
  RANGE_OPTIONS,
  SERVICE_OPTIONS,
  SOURCE_OPTIONS,
  sinceFor,
  urlValue,
  type DetectionsSearch,
} from '../lib/listSearch';

type Row = Detection & Partial<Pick<CrossClientDetection, 'client_tenant_id' | 'display_name'>>;

/**
 * Header and row share one template, or the columns drift apart. On a
 * phone the row is the detection and its score; asset and outcome fold
 * into the meta line rather than forcing a sideways scroll (task 75).
 */
const COLS =
  'grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,2.4fr)_1fr_minmax(0,1.1fr)_auto] items-center gap-3';

export function DetectionsList() {
  const { tenantId, setTenantId, orgId, selectedOrg } = useSelectedTenant();
  const idToken = useAccessToken();
  const navigate = useNavigate({ from: '/detections' });
  const search = useSearch({ from: '/detections' });
  const allClients = !tenantId;
  const org = allClients ? (orgId ?? undefined) : undefined;

  const severity: SeverityFilterValue = search.severity ?? 'All';
  const outcome = search.outcome ?? '';
  const source = search.source ?? '';
  const service = search.service ?? '';
  const range = search.range ?? 'all';
  const incident = search.incident;

  const setFilter = (patch: Partial<DetectionsSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const setSeverity = (value: SeverityFilterValue) =>
    setFilter({ severity: value === 'All' ? undefined : value });

  const query = useInfiniteQuery({
    queryKey: [
      'detections',
      tenantId || 'all',
      { severity, outcome, source, service, range, incident, org },
    ],
    queryFn: ({ pageParam }): Promise<{ items: Row[]; next_cursor: string | null }> => {
      const params: DetectionListParams = {
        severity: severity === 'All' ? undefined : severity,
        outcome: outcome || undefined,
        source: source || undefined,
        service: (service || undefined) as DetectionListParams['service'],
        incident,
        since: sinceFor(range),
        cursor: pageParam,
        limit: 50,
      };
      return tenantId
        ? listDetections(tenantId, params, idToken)
        : listAllDetections({ ...params, org }, idToken);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: Boolean(idToken),
  });

  const rows: Row[] = query.data?.pages.flatMap((p) => p.items) ?? [];
  const filtered =
    severity !== 'All' ||
    Boolean(outcome) ||
    Boolean(source) ||
    Boolean(service) ||
    range !== 'all' ||
    Boolean(incident);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Detections</h1>
          {allClients && (
            <p className="text-sm text-white/70">
              Showing {org ? `all of ${orgLabel(selectedOrg)}` : 'all Clients'} · merged by time
            </p>
          )}
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
          {/* On a phone severity is a select like the rest; at sm+ it is the pill rail below. */}
          <SeveritySelect value={severity} onChange={setSeverity} />
          <select
            aria-label="Outcome"
            value={outcome}
            onChange={(e) => setFilter({ outcome: urlValue(e.target.value) })}
            className={filterSelectClass}
          >
            {OUTCOME_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="text-bluesphere-textOnLight">
                {o.label}
              </option>
            ))}
          </select>
          <select
            aria-label="Source"
            value={source}
            onChange={(e) => setFilter({ source: urlValue(e.target.value) })}
            className={filterSelectClass}
          >
            {SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="text-bluesphere-textOnLight">
                {o.label}
              </option>
            ))}
          </select>
          <select
            aria-label="Service"
            value={service}
            onChange={(e) => setFilter({ service: urlValue(e.target.value) })}
            className={filterSelectClass}
          >
            {SERVICE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="text-bluesphere-textOnLight">
                {o.label}
              </option>
            ))}
          </select>
          <select
            aria-label="Date range"
            value={range}
            onChange={(e) =>
              setFilter({ range: urlValue(e.target.value, 'all') as DetectionsSearch['range'] })
            }
            className={filterSelectClass}
          >
            {RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="text-bluesphere-textOnLight">
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <SeverityPills value={severity} onChange={setSeverity} />

      {incident && (
        <div className="flex items-center gap-2 text-sm text-white/80">
          <span>
            Incident <span className="font-mono">{incident}</span>
          </span>
          <button
            type="button"
            className="rounded-full bg-white/10 px-2 py-0.5 text-xs hover:bg-white/20"
            onClick={() => setFilter({ incident: undefined })}
            aria-label="Clear incident filter"
          >
            clear
          </button>
        </div>
      )}

      <div className="bs-card p-5">
        <div
          className={`hidden md:grid ${COLS} px-4 py-2 text-xs font-semibold uppercase tracking-wider text-bluesphere-muted`}
        >
          <div>Detection</div>
          <div>Asset</div>
          <div>Outcome</div>
          <div className="text-right">Score</div>
        </div>

        {query.isPending ? (
          <Loading height="h-40" />
        ) : query.isError ? (
          <ErrorState
            title="Could not load detections"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title={filtered ? 'No detections match these filters' : 'No detections yet'}
            body={
              filtered
                ? 'Nothing matches the current filters.'
                : allClients
                  ? 'Nothing has been ingested for any visible Client yet.'
                  : 'Nothing has been ingested for this Client. Check ingestion health on the Overview.'
            }
            action={
              filtered ? (
                <PillButton
                  variant="outline"
                  size="sm"
                  onClick={() => void navigate({ search: {}, replace: true })}
                >
                  Clear filters
                </PillButton>
              ) : undefined
            }
          />
        ) : (
          <>
            <div className="bs-scroll max-h-[620px] space-y-1 overflow-y-auto pr-1">
              {rows.map((d) => (
                <div
                  key={`${d.client_tenant_id ?? tenantId}:${d.detection_id}`}
                  className={`bs-row grid ${COLS} px-4 py-2.5`}
                >
                  <div className="min-w-0">
                    <Link
                      to="/detections/$detectionId"
                      params={{ detectionId: d.detection_id }}
                      onClick={
                        allClients && d.client_tenant_id
                          ? () => setTenantId(d.client_tenant_id as string)
                          : undefined
                      }
                      className="bs-stretched-link block truncate text-sm font-semibold hover:underline"
                    >
                      {d.title}
                    </Link>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {allClients && d.display_name && <ClientBadge name={d.display_name} />}
                      <SeverityChip severity={d.severity} />
                      <SourceBadge label={sourceLabel(d.source)} />
                      <span className="text-xs text-bluesphere-muted" title={d.first_seen}>
                        {timeAgo(d.first_seen)}
                      </span>
                      {d.backfill && (
                        <span className="text-xs text-bluesphere-muted">· back-fill</span>
                      )}
                    </div>
                    {/* The desktop columns, as a second meta line on a phone. */}
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs md:hidden">
                      <OutcomeCell detection={d} compact />
                      {d.asset_name && (
                        <span className="min-w-0 truncate text-bluesphere-muted">
                          · {d.asset_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="hidden truncate text-sm text-bluesphere-textSecondary md:block">
                    {d.asset_name ?? '—'}
                  </div>
                  <div className="hidden min-w-0 md:block">
                    <OutcomeCell detection={d} />
                  </div>
                  <div
                    className="text-right text-lg font-bold tabular-nums"
                    style={{ color: SEVERITY_COLORS[d.severity] }}
                  >
                    {d.score}
                  </div>
                </div>
              ))}
            </div>
            {query.hasNextPage && (
              <div className="mt-3 flex justify-center">
                <PillButton
                  variant="outline"
                  size="sm"
                  onClick={() => void query.fetchNextPage()}
                  disabled={query.isFetchingNextPage}
                >
                  {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </PillButton>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * "Raised → BSM-12" or "Resolved by default-resolve": what the platform
 * decided. `compact` is the phone shape: one line of small text, the
 * second-line detail folded in after a separator.
 */
function OutcomeCell({
  detection: d,
  compact = false,
}: {
  detection: Detection;
  compact?: boolean;
}) {
  const wrap = compact
    ? 'min-w-0 truncate text-xs text-bluesphere-textSecondary'
    : 'truncate text-sm text-bluesphere-textSecondary';
  const detail = compact
    ? 'text-bluesphere-muted'
    : 'block truncate font-mono text-xs text-bluesphere-muted';
  if (d.short_id) {
    return (
      <div className={wrap}>
        Raised →{' '}
        <Link
          to="/alerts/$alertRef"
          params={{ alertRef: d.short_id }}
          className="bs-raise font-mono font-semibold text-bluesphere-primary hover:underline"
        >
          {d.short_id}
        </Link>
        {d.provider_status === 'closed' && (
          <span
            className={compact ? 'text-bluesphere-muted' : 'block text-xs text-bluesphere-muted'}
          >
            {compact ? ' · ' : ''}closed by provider
          </span>
        )}
      </div>
    );
  }
  return (
    <div className={wrap}>
      {outcomeLabel(d.outcome)}
      {d.fired_rule_id && (
        <span className={detail}>
          {compact ? ' · ' : ''}by <span className="font-mono">{d.fired_rule_id}</span>
        </span>
      )}
    </div>
  );
}
